import React, { useMemo, useState } from 'react';
import { MdInfo, MdRefresh } from 'react-icons/md';
import {
  Box,
  DrawerBody,
  DrawerHeader,
  Flex, HStack,
  Icon,
  IconButton,
  Text, Tooltip
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import DropdownMenu from '../../../../../components/DropdownMenu';
import { analyticsIds } from '../../../../../constants/analyticsIds';
import { useInsuredActiveRecordsData } from '../../../../../services/insured-service';
import insuredKeys from '../../../../../services/models/insured/keys';
import { getInfiniteListData } from '../../../../../services/util';
import colors from '../../../../../styles/theme/foundation/colors';
import { createMap } from '../../../../../utils/arrayUtil';
import { queryClient } from '../../../../../utils/queryClientUtil';

import CloseArrow from './components/CloseArrow';
import EmailHistoryBody from './components/EmailHistoryBody';

const CorrespondenceTray = ({ title, icon, insuredId, recordId }) => {
  const initialRecordId = recordId;
  const { data: activeRecords, isLoading: recordsLoading } = useInsuredActiveRecordsData({ insuredId });

  const jobs = useMemo(() => {
    if (recordsLoading) {
      return [];
    }

    return getInfiniteListData(activeRecords).map(record => ({
      jobName: record.job.jobName,
      id: record.id,
      contractNumber: record.contractNumber
    }));
  }, [activeRecords]);

  const [currentRecordId, setCurrentRecordId] = useState(initialRecordId);
  const [currentOrder, setCurrentOrder] = useState('desc');

  const recordsMap = jobs && jobs.map(({ id, jobName, contractNumber }) => {
    let label = jobName;

    if (contractNumber) {
      label = `${jobName} - ${contractNumber}`;
    }

    return {
      id,
      label
    };
  });
  const recordOptions = [{ id: 'all', label: 'All Records' }, ...recordsMap];
  const recordOptionsHash = createMap(recordOptions, 'id');

  const orderOptions = [
    {
      label: 'Newest First',
      id: 1,
      type: 'desc'
    },
    {
      label: 'Oldest First',
      id: 2,
      type: 'asc'
    }
  ];
  const orderOptionsHash = createMap(orderOptions, 'type');

  const handleProjectOptionClick = selected => {
    setCurrentRecordId(selected.value);
  };

  const handleOrderOptionsClick = selected => {
    if (parseInt(selected.value, 10) === 1) {
      setCurrentOrder('desc');
    } else {
      setCurrentOrder('asc');
    }
  };

  return (
    <>
      <CloseArrow />
      <DrawerHeader>
        <Flex justify="space-between" w="100%">
          <Flex direction="column">
            <HStack>
              <Text lineHeight="35px" textStyle="displayXxs">
                {title}
              </Text>
              <Box>
                <Tooltip label={'This tray shows emails sent to external contacts through PINS, including both manual messages and configured automations. '
                  + 'These are intentional, outbound communications initiated by your team.'}
                >
                  <Box>
                    <Icon as={MdInfo} color={colors.greyScale['600']} h="16px" w="16px" />
                  </Box>
                </Tooltip>
              </Box>
            </HStack>
            <Flex mt={2}>
              <IconButton
                data-analytics-id={analyticsIds.insuredProfilePage.policiesComponent.emailHistoryTray.refreshEmailHistoryButton}
                icon={<Icon as={MdRefresh} h="16px" w="16px" />}
                mr="2px"
                variant="textGrey"
                onClick={() => {
                  const emailKey = insuredKeys.insuredEmails(insuredId, currentRecordId, currentOrder);

                  queryClient.invalidateQueries(emailKey);
                }}
              />
              <DropdownMenu
                dataAnalyticsKey={analyticsIds.insuredProfilePage.policiesComponent.emailHistoryTray.sortEmailHistory}
                options={orderOptions}
                selected={orderOptionsHash[currentOrder].label}
                variant="secondary"
                width="140px"
                onSelect={handleOrderOptionsClick}
              />
            </Flex>
          </Flex>
          <DropdownMenu
            dataAnalyticsKey={analyticsIds.insuredProfilePage.policiesComponent.emailHistoryTray.sortEmailHistoryByJob}
            options={recordOptions}
            selected={recordOptionsHash[currentRecordId]?.label}
            size="lg"
            variant="secondary"
            width="240px"
            onSelect={handleProjectOptionClick}
          />
        </Flex>
      </DrawerHeader>
      <DrawerBody>
        <EmailHistoryBody
          insuredId={insuredId}
          order={currentOrder}
          recordId={parseInt(currentRecordId, 10)}
        />
      </DrawerBody>
    </>
  );
};

CorrespondenceTray.propTypes = {
  insuredId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  title: PropTypes.string,
  icon: PropTypes.func
};

export default CorrespondenceTray;
